/**
 * ThemeV2EditorDialog - Full-screen dialog for editing a V2 theme snapshot.
 *
 * Wraps ThemeV2EditorView in a dialog with Save/Cancel.
 * Supports editing individual screen templates via OnboardingScreenEditorDialogV2.
 * Works with any ThemeV2-compatible data (project themes, strategy themes).
 *
 * Screen edit flow:
 * 1. User clicks a screen in the preview carousel → onScreenEdit fires
 * 2. OnboardingScreenEditorDialogV2 opens with that StepNode
 * 3. User edits content/styling → saves
 * 4. flowDocument.screens.{screenId} updated in local theme state
 * 5. Carousel refreshes to show changes
 *
 * @module themes/components
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  IconButton,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { ThemeV2EditorView } from '@modules/themes/components/ThemeV2EditorView';
import { GenerateThemeFromImageDialog } from '@modules/themes/components/GenerateThemeFromImageDialog';
import { GeneratedThemeMetadataPanel } from '@modules/themes/components/GeneratedThemeMetadataPanel';
import { OnboardingScreenEditorDialogV2 } from '@modules/reactFlow/components/onboarding/OnboardingScreenEditorDialogV2';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { StepNode } from '@segmently/schema-models/v2';
import type { CreateThemeV2Input, ThemeV2 } from '@modules/themes/types/themesV2.types';
import { createEditableThemeV2FromDraft } from '@modules/themes/utils';
import {
  FULLSCREEN_DIALOG_BUTTON_SX,
  FULLSCREEN_DIALOG_HEADER_SX,
  FULLSCREEN_DIALOG_ICON_BUTTON_SX,
  FULLSCREEN_DIALOG_PAPER_SX,
  FULLSCREEN_DIALOG_TITLE_SX,
} from '@modules/themes/components/fullscreenDialogChrome';

export interface ThemeV2EditorDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Theme data to edit */
  theme: ThemeV2 | null;
  /** Project id for image analysis and asset upload. Enables Generate From Image. */
  projectId?: string;
  /** Dialog title */
  title?: string;
  /** Close without saving */
  onClose: () => void;
  /** Save the modified theme */
  onSave: (updatedTheme: ThemeV2) => Promise<void>;
  /** Optional secondary submit action for generated drafts. Receives current edited state. */
  onApply?: (updatedTheme: ThemeV2) => Promise<void>;
  /** Label for the save button. */
  saveLabel?: string;
  /** Loading label for the save button. */
  savingLabel?: string;
  /** Label for the optional apply button. */
  applyLabel?: string;
  /** Loading label for the optional apply button. */
  applyingLabel?: string;
  /** Optional verification panel shown above the editable preview. */
  metadataPanel?: React.ReactNode;
}

/**
 * Full-screen dialog wrapper for ThemeV2EditorView with screen template editing.
 */
export const ThemeV2EditorDialog: React.FC<ThemeV2EditorDialogProps> = ({
  open,
  theme,
  projectId,
  title = 'Edit Theme',
  onClose,
  onSave,
  onApply,
  saveLabel = 'Save',
  savingLabel = 'Saving...',
  applyLabel = 'Apply',
  applyingLabel = 'Applying...',
  metadataPanel,
}) => {
  const [editedTheme, setEditedTheme] = useState<ThemeV2 | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);

  // Screen template editor state
  const [screenEditorOpen, setScreenEditorOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<FlowStepNode | null>(null);
  const [carouselKey, setCarouselKey] = useState(0);

  // Ref for stable callback access (avoids stale closure in memoized handlers)
  const stateRef = useRef<{
    setEditedTheme: React.Dispatch<React.SetStateAction<ThemeV2 | null>>;
    setHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
    setCarouselKey: React.Dispatch<React.SetStateAction<number>>;
    setScreenEditorOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setEditingStep: React.Dispatch<React.SetStateAction<FlowStepNode | null>>;
  } | null>(null);

  useEffect(() => {
    stateRef.current = {
      setEditedTheme,
      setHasChanges,
      setCarouselKey,
      setScreenEditorOpen,
      setEditingStep,
    };
  });

  // Reset local state when theme changes or dialog opens
  useEffect(() => {
    if (open && theme) {
      setEditedTheme(JSON.parse(JSON.stringify(theme)));
      setHasChanges(false);
      setSaving(false);
      setApplying(false);
      setCarouselKey(0);
      setGenerateOpen(false);
    }
  }, [open, theme]);

  const handleChange = useCallback((updatedTheme: ThemeV2) => {
    setEditedTheme(updatedTheme);
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!editedTheme) return;
    setSaving(true);
    try {
      await onSave(editedTheme);
      setHasChanges(false);
      onClose();
    } catch (err) {
      console.error('[ThemeV2EditorDialog] Save failed:', err);
    } finally {
      setSaving(false);
    }
  }, [editedTheme, onSave, onClose]);

  const handleApply = useCallback(async () => {
    if (!editedTheme || !onApply) return;
    setApplying(true);
    try {
      await onApply(editedTheme);
      setHasChanges(false);
      onClose();
    } catch (err) {
      console.error('[ThemeV2EditorDialog] Apply failed:', err);
    } finally {
      setApplying(false);
    }
  }, [editedTheme, onApply, onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleOpenGenerate = useCallback(() => {
    setGenerateOpen(true);
  }, []);

  const handleApplyGeneratedTheme = useCallback(async (draft: CreateThemeV2Input) => {
    setEditedTheme((currentTheme) => createEditableThemeV2FromDraft(draft, {
      baseTheme: currentTheme,
      fallbackId: theme?.id ?? 'theme-v2-editor-draft',
      fallbackName: theme?.name ?? 'Theme',
      preserveBaseName: true,
    }));
    setHasChanges(true);
    setCarouselKey((prev) => prev + 1);
  }, [theme?.id, theme?.name]);

  // ── Screen template editing ──

  /** User clicked a screen in the preview carousel */
  const handleScreenEdit = useCallback((screenId: string, step: StepNode) => {
    const flowStep: FlowStepNode = {
      ...step,
      id: screenId,
      entityId: screenId,
    };
    setEditingStep(flowStep);
    setScreenEditorOpen(true);
  }, []);

  /** Screen saved — update in theme's FlowDocument */
  const handleStepSaved = useMemo(() => {
    return (step: FlowStepNode) => {
      if (!step?.id) return;

      const state = stateRef.current;
      if (!state) return;

      // Update screen in flowDocument.screens
      state.setEditedTheme((prev) => {
        if (!prev) return prev;
        const updated = JSON.parse(JSON.stringify(prev)) as ThemeV2;
        if (updated.flowDocument?.screens) {
          (updated.flowDocument.screens as Record<string, unknown>)[step.id] = step;
        }
        return updated;
      });

      state.setHasChanges(true);
      state.setCarouselKey((prev) => prev + 1);
      state.setScreenEditorOpen(false);
      state.setEditingStep(null);
    };
  }, []);

  const handleScreenEditorClose = useCallback(() => {
    setScreenEditorOpen(false);
    setEditingStep(null);
  }, []);

  if (!editedTheme) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        sx={FULLSCREEN_DIALOG_PAPER_SX}
        data-testid="project-theme-editor-dialog"
      >
        <Box component="header" sx={FULLSCREEN_DIALOG_HEADER_SX}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', zIndex: 1 }}>
            <IconButton
              onClick={handleClose}
              size="small"
              aria-label="close dialog"
              sx={{
                ...FULLSCREEN_DIALOG_ICON_BUTTON_SX,
                backgroundColor: (theme) => theme.palette.action.hover,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              ...FULLSCREEN_DIALOG_TITLE_SX,
              minWidth: 0,
            }}
          >
            {title}: {editedTheme.name}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto', zIndex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Click a screen to edit its template
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!hasChanges || saving || applying}
              sx={FULLSCREEN_DIALOG_BUTTON_SX}
              data-testid="project-theme-editor-save-btn"
            >
              {saving ? savingLabel : saveLabel}
            </Button>
            {onApply && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={handleApply}
                disabled={saving || applying}
                sx={FULLSCREEN_DIALOG_BUTTON_SX}
                data-testid="project-theme-editor-apply-btn"
              >
                {applying ? applyingLabel : applyLabel}
              </Button>
            )}
          </Stack>
        </Box>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            {editedTheme.generatedMetadata?.sourceKind === 'image-analysis'
              ? <GeneratedThemeMetadataPanel theme={editedTheme} />
              : metadataPanel}
            <ThemeV2EditorView
              theme={editedTheme}
              onChange={handleChange}
              onScreenEdit={handleScreenEdit}
              carouselKey={carouselKey}
              projectId={projectId}
              settingsPanelFooter={projectId ? (
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleOpenGenerate}
                  disabled={saving || applying}
                  fullWidth
                  data-testid="theme-v2-editor-generate-from-image-btn"
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Generate From Image
                </Button>
              ) : undefined}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* V2 Screen template editor dialog */}
      <OnboardingScreenEditorDialogV2
        open={screenEditorOpen}
        onClose={handleScreenEditorClose}
        stepData={editingStep}
        onStepSaved={handleStepSaved}
        defaultLanguage={editedTheme.flowDocument?.defaultLanguage || 'en-US'}
        languages={editedTheme.flowDocument?.languages || ['en-US']}
        projectId={projectId || 'theme-v2-assets'}
        onboardingId={editedTheme.id || 'theme-edit'}
        versionId="theme-v2-edit"
        flowDocument={editedTheme.flowDocument}
      />
      {projectId && editedTheme && (
        <GenerateThemeFromImageDialog
          open={generateOpen}
          projectId={projectId}
          baseTheme={editedTheme}
          createActionLabel="Apply to Editor"
          onClose={() => setGenerateOpen(false)}
          onCreateTheme={handleApplyGeneratedTheme}
        />
      )}
    </>
  );
};
